import { AnyDiagramEntry, PointType } from '@shared/types/geoTypes'

import { PaginationResponse, backendInstance } from './base'

export const BASE_URL = 'collections'
export const AREA_SEARCH_BASE_URL = 'geo'

// #region Info

export type UserType = {
  id: number
  phoneNumber: string
  isMale: boolean | null
  age: number | null
  point: PointType | null
  sourceName: string
  fullAddress: string
  cleanedAddress: string | null
  telegramId: string | null
  isWhastsApp: boolean
  isViber: boolean
}

export type SearchResponse = PaginationResponse<UserType[]>

export type CollectionInfoResponse = {
  entries: SearchResponse
  phonesCount: number
  entriesCount: number
  collectionName: string
}

export type CollectionInfoWithDiagramsResponse = CollectionInfoResponse & {
  ageDiagram: AnyDiagramEntry
  genderDiagram: AnyDiagramEntry
}

export type CollectionInfoPayload = {
  collectionId: string
  pageNumber: number
  pageSize: number
  addressPart?: string
  isMale?: boolean
  ageMin?: number
  ageMax?: number
}

export const getCollectionInfoCall = async (
  payload: CollectionInfoPayload
): Promise<CollectionInfoResponse> => {
  try {
    const response = await backendInstance.get(`${BASE_URL}/get/content`, {
      params: {
        collectionId: payload.collectionId,
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize
      }
    })
    return response.data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

export const getCollectionInfoWithDiagramsCall = async (
  payload: CollectionInfoPayload
): Promise<CollectionInfoWithDiagramsResponse> => {
  try {
    const response = await backendInstance.get(
      `${BASE_URL}/get/content/with/diagrams`,
      {
        params: {
          collectionId: payload.collectionId,
          pageNumber: payload.pageNumber,
          pageSize: payload.pageSize
        }
      }
    )
    return response.data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}
// #endregion

// #region List
export type UserCollectionType = {
  id: string
  name: string
  creationDate: string
  phonesCount: number
  entriesCount: number
  ageDiagram: Array<AnyDiagramEntry>
  genderDiagram: Array<AnyDiagramEntry>
}

export type UsersByAddressesType = {
  fullAddress: string
  users: UserType[]
  allUsersCount: number
}

export type UserCollectionCollapsedType = {
  addressWithUsersGroups: UsersByAddressesType[]
  ageDiagramData: Array<AnyDiagramEntry>
  genderDiagramData: Array<AnyDiagramEntry>
  totalCount: number
}

export type UserCollectionApiResponse = PaginationResponse<UserCollectionType[]>
export type UserCollectionCollapsedApiResponse =
  Array<UserCollectionCollapsedType>

export type UserCollectionInfo = UserCollectionApiResponse
export type UserCollectionInfoCollapsed = UserCollectionCollapsedApiResponse

export const getUserCollectionsListCall = async (
  page = 1,
  size = 50
): Promise<UserCollectionApiResponse> => {
  try {
    const response = await backendInstance.get(`${BASE_URL}/list`, {
      params: {
        pageNumber: page,
        pageSize: size
      }
    })
    return response.data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}
// #endregion

// #region Create
export type CreateUserCollectionRequestType = {
  collectionName: string
  items: Array<{
    source_name: string
    id: number
  }>
}

export const createUserCollectionCall = async (
  payload: CreateUserCollectionRequestType
): Promise<string> => {
  if (payload.items === null) return ''

  try {
    const response = await backendInstance.post(`${BASE_URL}/create`, {
      name: payload.collectionName,
      items: payload.items
    })
    return response?.data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}
// #endregion

// #region Remove

export type RemoveUserCollectionRequestType = {
  collectionId: string
}

export const removeUserCollectionCall = async (
  payload: RemoveUserCollectionRequestType
): Promise<string> => {
  try {
    const response = await backendInstance.post(
      `${BASE_URL}/delete?collection_id=${payload.collectionId}`
    )
    return response?.data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

// #endregion

// #region Users in area
export type GetUsersInCircleAreaRequest = {
  rSpLat?: number
  rSpLong?: number
  rEpLat?: number
  rEpLong?: number
  rectange?: number
  cLat: number | null
  cLong: number | null
  cRadius: number | null
  circle?: string
  query?: string
  gender: string | null
  age: string
  // messangers: string | null
  isClusterClick?: boolean
}

export type FoundUsersWithAddressGroupType = {
  fullAddress: string
  users: UserType[]
  allUsersCount: number
}

export type GeoSearchResultType = {
  addressWithUsersGroups: FoundUsersWithAddressGroupType[]
  ageDiagramData: AnyDiagramEntry[]
  genderDiagramData: AnyDiagramEntry[]
  totalCount: number | null
}
export type GetUsersInCircleAreaResponse = GeoSearchResultType

export const getUsersInCircleAreaCall = async ({
  cLat,
  cLong,
  cRadius,
  gender,
  age
}: // messangers
GetUsersInCircleAreaRequest): Promise<GeoSearchResultType> => {
  try {
    const response = await backendInstance.get(
      `${AREA_SEARCH_BASE_URL}/search/grouped`,
      {
        params: {
          cLat,
          cLong,
          cRadius,
          pageSize: 250,
          age: age,
          ...(gender !== 'any' && { gender }),
          leakDb: 'any'
          // ...(messangers !== 'any' && { leakDb: messangers })
        }
      }
    )
    const data: GeoSearchResultType = response.data
    if (!data || data.totalCount === 0) {
      return {
        addressWithUsersGroups: [],
        ageDiagramData: [],
        genderDiagramData: [],
        totalCount: 0
      }
    }

    return data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

export type GetUsersAndAdressesInCircleAreaRequest = {
  rSpLat?: number
  rSpLong?: number
  rEpLat?: number
  rEpLong?: number
  rectange?: number
  cLat: number | null
  cLong: number | null
  cRadius: number | null
  circle?: string
  query?: string
  gender: string | null
  age: string
  messangers: string | null
  isClusterClick?: boolean
}

export type AddressType = {
  address: string
  entriesCount: number
}

export type GeoSearchSeparatedResultType = {
  users: UserType[]
  addressInfos: AddressType[]
  ageDiagramData: AnyDiagramEntry[]
  genderDiagramData: AnyDiagramEntry[]
  totalUsersCount: number
  totalAddressCount: number
  isSearchedInTooBigCluster: boolean
}

export const getUsersAndAddressesInCircleAreaCall = async ({
  cLat,
  cLong,
  cRadius,
  gender,
  age
}: // messangers
GetUsersInCircleAreaRequest): Promise<GeoSearchSeparatedResultType> => {
  try {
    const response = await backendInstance.get(
      `${AREA_SEARCH_BASE_URL}/search/separated/addresses`,
      {
        params: {
          cLat,
          cLong,
          cRadius,
          pageSize: 250,
          age: age,
          ...(gender !== 'any' && { gender }),
          // ...(messangers !== 'any' && { leakDb: messangers })
          leakDb: 'any'
        }
      }
    )
    const data: GeoSearchSeparatedResultType = response.data
    if (!data || data.totalUsersCount === 0) {
      return {
        addressInfos: [],
        ageDiagramData: [],
        genderDiagramData: [],
        users: [],
        totalAddressCount: 0,
        totalUsersCount: 0,
        isSearchedInTooBigCluster: false
      }
    }

    return data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

// #endregion

// #region Update

export type UpdateUserCollectionRequestType = {
  collectionId: string
  name: string
}
export const updateUserCollectionCall = async (
  payload: UpdateUserCollectionRequestType
): Promise<string> => {
  try {
    const response = await backendInstance.post(`${BASE_URL}/update`, {
      name: payload.name,
      collectionId: payload.collectionId
    })
    return response?.data
  } catch (error) {
    throw error
    // return await Promise.reject(
    //   error?.message ?? 'Проблема соединения с сервером'
    // )
  }
}

// #endregion

// #region Save as file

export type CollectionSaveResponse = {
  file: File
}

export type CollectionSavePayload = {
  collectionId: string
}

export const downloadCollectionReportCall = async (
  payload: CollectionSavePayload
): Promise<CollectionSaveResponse> => {
  try {
    const response = await backendInstance.get(`${BASE_URL}/report/pdf`, {
      params: {
        collectionId: payload.collectionId
      }
    })
    const data = response.data
    return data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

// #endregion

// #region Report

export type GetUsersReportRequest = {
  rSpLat?: number
  rSpLong?: number
  rEpLat?: number
  rEpLong?: number
  rectange?: number
  cLat: number | null
  cLong: number | null
  cRadius: number | null
  circle?: string
  query?: string
  gender: string | null
  age: string
  // messangers: string | null
  isClusterClick?: boolean
}
export const getUsersReporCall = async ({
  cLat,
  cLong,
  cRadius,
  gender,
  age
}: GetUsersReportRequest): Promise<Blob> => {
  try {
    const response = await backendInstance.get(
      `${AREA_SEARCH_BASE_URL}/report/area/analytics`,
      {
        responseType: 'blob',
        params: {
          cLat,
          cLong,
          cRadius,
          pageSize: 250,
          age: age,
          ...(gender !== 'any' && { gender }),
          // ...(messangers !== 'any' && { leakDb: messangers })
          leakDb: 'any'
        }
      }
    )
    const data = response.data
    return data
  } catch (error: unknown) {
    return await Promise.reject(error)
  }
}

// #endregion
